import React from 'react';
import { BaseLayout, type NavItem } from './BaseLayout';
import { layoutConfigs } from './layoutConfigs';
import { useAuthStore } from '../../stores/authStore';

export type LayoutType = keyof typeof layoutConfigs;

interface SidebarLayoutProps {
  type: LayoutType;
  children: React.ReactNode;
}

/** Role hierarchy: higher index = more access */
const ADMIN_ROLE_LEVEL: Record<string, number> = {
  marketer: 1,
  super_admin: 2,
};

function filterNavByRole(items: NavItem[], adminRole: string | null | undefined, isSuperAdmin: boolean): NavItem[] {
  const userLevel = isSuperAdmin
    ? ADMIN_ROLE_LEVEL.super_admin
    : ADMIN_ROLE_LEVEL[adminRole ?? ''] ?? 0;

  return items.filter((item) => {
    const requiredLevel = ADMIN_ROLE_LEVEL[item.minRole ?? 'super_admin'] ?? ADMIN_ROLE_LEVEL.super_admin;
    return userLevel >= requiredLevel;
  });
}

export function SidebarLayout({ type, children }: SidebarLayoutProps) {
  const config = layoutConfigs[type];
  const user = useAuthStore((s) => s.user);

  if (!config) {
    throw new Error(`Layout configuration not found for type: ${type}`);
  }

  const navItems = React.useMemo(() => {
    if (type !== 'admin') return config.navItems;
    return filterNavByRole(config.navItems, (user as any)?.adminRole, !!(user as any)?.isSuperAdmin);
  }, [type, config.navItems, user]);

  return (
    <BaseLayout
      navItems={navItems}
      sidebarTitle={config.sidebarTitle}
      sidebarDescription={config.sidebarDescription}
      headerSubtitle={config.headerSubtitle}
      rootPath={config.rootPath}
    >
      {children}
    </BaseLayout>
  );
}
